import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { PageHeader } from '@/components/PageHeader';
import { Box, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, LoaderFnContext } from '@tanstack/react-router';
import { TenantUsageLimitsTable } from './-components/TenantUsageLimitsTable';
import { ProjectUsageLimitsTable } from './-components/ProjectUsageLimitsTable';
import { fmtCurrency } from '@/lib/currency';

export const Route = createFileRoute('/_main/tenant/limits/')({
  loader,
  component: Component,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const projects = await adminClient.admin_v1_get_all_projects();
  const tenant = await adminClient.settings_v1_get_tenant();
  return {
    tenant,
    projects,
    adminClient,
  };
}

function Component() {
  const { tenant, projects, adminClient } = Route.useLoaderData();
  const currency = tenant.spec.billing_settings.currency;
  // 0 は上限なしを意味するので、金額としては表示しない
  const fmtMonthlyBill = (v: number | null | undefined) =>
    v ? `${fmtCurrency(v, currency)} / 月` : null;

  return (
    <MainContainer>
      <PageHeader
        title="利用上限設定"
        description="テナント および プロジェクトの月単位の予算設定を行います。"
      ></PageHeader>
      <Stack gap="60px">
        <Box>
          <Title order={4} my="lg">
            テナント予算設定
          </Title>
          <Text size="sm" mt="md" mb="xl">
            テナント全体の月の課金額に対するアラートと利用上限を設定します。
          </Text>
          <Box my="lg">
            <TenantUsageLimitsTable
              tenant={tenant}
              adminClient={adminClient}
            ></TenantUsageLimitsTable>
          </Box>
          <Text size="xs">
            ※ クォータよりも大きな値を設定した場合、クォータの値が上限として適用されます。
          </Text>
        </Box>
        <Box>
          <Title order={4} my="lg">
            プロジェクト予算設定
          </Title>
          <Text size="sm" mt="md" mb="xl">
            プロジェクトごとの月の課金額に対するアラートと利用上限を設定します。
          </Text>
          <Box my="lg">
            <ProjectUsageLimitsTable
              tenant={tenant}
              projects={projects.items}
              adminClient={adminClient}
            ></ProjectUsageLimitsTable>
          </Box>
          <Text size="xs">
            ※
            クォータやテナント予算設定よりも大きな値を設定した場合、それらの値が上限として適用されます。
          </Text>
        </Box>
        <Box>
          <Title order={4} my="lg">
            テナント利用上限（クォータ）
          </Title>
          <Box ml="lg" my="lg">
            <p>{fmtMonthlyBill(tenant.spec.quotas?.monthly_bill) ?? '上限なし'}</p>
          </Box>
          <Text size="xs">
            ※ 月の課金額がこの値を超えると、API
            の実行ができなくなります。上限の引き上げには、クォータの変更リクエストが必要です。
          </Text>
        </Box>
      </Stack>
    </MainContainer>
  );
}
